import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { StepSkip, StepSkipPreview } from "./types";

/** Steps that can be passed over: a decision has more than one way on and an exit has none. */
export const skippableType = (type: string) => type !== "branch" && type !== "exit";

const n = (x: number | undefined, one: string, many: string) => `${x ?? 0} ${(x ?? 0) === 1 ? one : many}`;

/** "Skip this step for everyone" on one step of the automation page: turns the step off (or back on) for every version
 *  and every run, after a preview from the CRM (the email it skips, how many people are at it and before it). */
export function StepSkipLine({ automationId, stepKey, stepType, skip }: { automationId: number; stepKey: string; stepType: string; skip?: StepSkip }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [preview, setPreview] = useState<StepSkipPreview | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const turnOff = !skip;
  const call = useMutation({
    mutationFn: async (confirm: boolean) => {
      const body = { step_key: stepKey, skip: turnOff, ...(reason.trim() ? { reason: reason.trim() } : {}) };
      try {
        return await api(`/api/v1/automations/${automationId}/step-skips`, { json: confirm ? { ...body, confirm: true } : body });
      } catch (e) {
        if (!confirm && e instanceof ApiError && e.code === "confirm_required") {
          setPreview(e.details as StepSkipPreview);
          return null;
        }
        throw e;
      }
    },
    onSuccess: (res) => {
      if (res === null) return;
      setPreview(null);
      setReason("");
      setError("");
      toast(turnOff ? "Step turned off for everyone" : "Step turned back on");
      for (const k of ["automation", "automation-people", "automation-activity", "automation-run"]) void qc.invalidateQueries({ queryKey: [k] });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : String(e);
      if (preview) setError(msg);
      else toast(msg, "error");
    },
  });
  if (!skip && !skippableType(stepType)) return null;
  const pv = preview;
  return (
    <div className="ml-6 mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
      {skip && (
        <Badge variant="warning" title={skip.reason ? `Why: ${skip.reason}` : undefined}>
          Off for everyone since {formatDate(skip.at)} (by {skip.by})
        </Badge>
      )}
      <Button size="sm" variant="link" className="h-6 px-1 text-xs" disabled={call.isPending} onClick={() => call.mutate(false)}>
        {skip ? "Turn back on" : "Skip this step for everyone"}
      </Button>
      <Dialog open={!!pv} onOpenChange={(o) => !o && (setPreview(null), setError(""))}>
        <DialogContent>
          <DialogTitle>{turnOff ? "Skip this step for everyone?" : "Turn this step back on?"}</DialogTitle>
          <DialogDescription>
            {pv?.step_label}
            {pv?.subject && !pv.step_label.includes(pv.subject) ? ` (email “${pv.subject}”)` : ""}
          </DialogDescription>
          {pv && (
            <div className="space-y-2 text-sm">
              {turnOff ? (
                <p>{pv.subject ? `Nobody gets the email “${pv.subject}” from this automation until it is turned back on.` : "Nobody goes through this step until it is turned back on."} This covers every version and everyone in it now or later.</p>
              ) : (
                <p>
                  Turned off by {pv.turned_off_by} on {pv.turned_off_on}. People who haven't passed it yet get it again when they reach it.
                </p>
              )}
              <ul className="list-disc pl-5 text-[var(--color-muted-foreground)]">
                <li>{n(pv.people_at, "person is", "people are")} waiting at it now{turnOff ? " (they move on to the next step)" : ""}.</li>
                <li>{n(pv.people_before, "person is", "people are")} before it{turnOff ? " (they pass straight over it)" : " (they get it when they reach it)"}.</li>
                {!!((pv.shadow_at ?? 0) + (pv.shadow_before ?? 0)) && <li>{n((pv.shadow_at ?? 0) + (pv.shadow_before ?? 0), "shadow run", "shadow runs")} too (they only log).</li>}
              </ul>
              {pv.note && <p className="text-xs text-[var(--color-warning-fg)]">{pv.note}</p>}
              {turnOff && (
                <label className="block text-xs">
                  Why (optional, kept with the change)
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. outdated offer" className="mt-1" />
                </label>
              )}
              {error && <p className="text-xs text-[var(--color-danger-fg)]">{error}</p>}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>
              Not now
            </Button>
            <Button disabled={call.isPending} onClick={() => call.mutate(true)}>
              {turnOff ? "Skip it for everyone" : "Turn it back on"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
